import { useTranslation } from 'react-i18next';

import { DynamicFormModal } from '@/shared/components/dynamic-form/DynamicFormModal/DynamicFormModal';
import { Callout } from '@/shared/components/feedback/Callout/Callout';
import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';

import {
  useEntitlementGrantModalLogic,
  type PromoGrantValues,
} from './useEntitlementGrantModalLogic';

/**
 * Comping an account — or replacing the comp it already has.
 *
 * Presentation only; which dialog this is, what it warns about and what it sends
 * are all in `useEntitlementGrantModalLogic` (U5).
 *
 * The warnings go in the modal's `banner` slot — **above** the fields — and not
 * in the form's `children`, which the kit puts under them. A notice saying "this
 * destroys the comp underneath it" has to be read before the plan is chosen, not
 * after the operator has finished typing the note. (The slot is new: the modal
 * supplies its own `renderLayout`, so a caller's was silently overwritten.)
 */
export function EntitlementGrantModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('payments');
  const view = useEntitlementGrantModalLogic({
    userId: typeof modalData?.['userId'] === 'string' ? modalData['userId'] : '',
    onDone: onCancel,
  });

  return (
    <DynamicFormModal<PromoGrantValues>
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
      title={view.title}
      description={view.description}
      size="lg"
      isLoading={view.isLoading}
      fields={view.fields}
      schema={view.schema}
      initialValues={view.initialValues}
      submitLabel={view.submitLabel}
      onCancel={onCancel}
      onSubmit={view.submit}
      banner={
        <div className="flex flex-col gap-md">
          <dl className="flex flex-wrap items-baseline gap-sm">
            <dt className="text-caption text-muted-foreground">{t('entitlements.grant.member')}</dt>
            {/* An address and a uuid are both machine strings that reorder around
                their punctuation under RTL, so the value is an LTR island (U7). */}
            <dd dir="ltr" className="min-w-0 truncate text-body">
              {view.memberLabel}
            </dd>
          </dl>

          {view.replacementWarning === null ? null : (
            <Callout tone="danger" liveness="alert" title={t('entitlements.grant.replace.title')}>
              <div className="flex flex-col gap-sm">
                <p>{view.replacementWarning}</p>
                <p>{view.noteFateWarning}</p>
              </div>
            </Callout>
          )}
        </div>
      }
    />
  );
}
